"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_SCALAR_STRING_LENGTH = exports.MAX_CLIENT_ATTRIBUTES = void 0;
exports.extractError = extractError;
exports.resolveErrorForEmission = resolveErrorForEmission;
exports.generateTelemetryEventId = generateTelemetryEventId;
exports.getClientLogLevel = getClientLogLevel;
exports.shouldEmitLogLevel = shouldEmitLogLevel;
exports.normalizeClientAttributes = normalizeClientAttributes;
exports.mergeCorrelationMetadata = mergeCorrelationMetadata;
const SENSITIVE_SUBSTRINGS = [
    "password",
    "token",
    "authorization",
    "cookie",
    "secret",
    "email",
    "ssn",
    "apikey",
];
exports.MAX_CLIENT_ATTRIBUTES = 20;
exports.MAX_SCALAR_STRING_LENGTH = 1024;
const LOG_LEVEL_RANK = {
    trace: 10,
    debug: 20,
    info: 30,
    warn: 40,
    error: 50,
    fatal: 60,
};
function isScalar(value) {
    return (typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean");
}
function truncateString(value) {
    if (value.length <= exports.MAX_SCALAR_STRING_LENGTH) {
        return value;
    }
    return `${value.slice(0, exports.MAX_SCALAR_STRING_LENGTH - 1)}…`;
}
function sanitizeScalar(value) {
    if (typeof value === "string") {
        return truncateString(value);
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "boolean") {
        return value;
    }
    return undefined;
}
function isSensitiveKey(key) {
    const normalized = key.toLowerCase().replace(/[_-]/g, "");
    return SENSITIVE_SUBSTRINGS.some(substring => normalized.includes(substring));
}
function extractError(data) {
    if (!data) {
        return undefined;
    }
    if (data.error instanceof Error) {
        return data.error;
    }
    if (data.err instanceof Error) {
        return data.err;
    }
    return undefined;
}
function resolveErrorForEmission(message, data) {
    return extractError(data) ?? new Error(message);
}
function generateTelemetryEventId() {
    if (typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
function isLogLevel(value) {
    return Object.hasOwn(LOG_LEVEL_RANK, value);
}
function getClientLogLevel() {
    const configured = typeof window !== "undefined" ? window.ENV?.LOG_LEVEL : undefined;
    const normalized = configured?.toLowerCase();
    if (normalized && isLogLevel(normalized)) {
        return normalized;
    }
    return "info";
}
const DEFAULT_LOG_LEVEL_RANK = 30;
function shouldEmitLogLevel(level) {
    const levelRank = LOG_LEVEL_RANK[level] ?? DEFAULT_LOG_LEVEL_RANK;
    const configuredRank = LOG_LEVEL_RANK[getClientLogLevel()] ?? DEFAULT_LOG_LEVEL_RANK;
    return levelRank >= configuredRank;
}
function normalizeClientAttributes(loggerName, message, data) {
    const attributes = {
        _log: loggerName,
        _timestamp: new Date().toISOString(),
        tag: typeof data?.tag === "string" ? data.tag : message,
    };
    if (!data) {
        return limitAttributes(attributes);
    }
    for (const [key, value] of Object.entries(data)) {
        if (key === "tag" || key === "error" || key === "err") {
            continue;
        }
        if (isSensitiveKey(key)) {
            continue;
        }
        const scalar = sanitizeScalar(value);
        if (scalar !== undefined) {
            attributes[key] = scalar;
        }
    }
    const error = extractError(data);
    if (error) {
        attributes.errorName = error.name;
        attributes.errorMessage = truncateString(error.message);
    }
    return limitAttributes(attributes);
}
function limitAttributes(attributes) {
    const entries = Object.entries(attributes);
    if (entries.length <= exports.MAX_CLIENT_ATTRIBUTES) {
        return attributes;
    }
    const limited = {};
    for (const [key, value] of entries.slice(0, exports.MAX_CLIENT_ATTRIBUTES)) {
        limited[key] = value;
    }
    return limited;
}
function mergeCorrelationMetadata(attributes, correlation) {
    if (!correlation) {
        return attributes;
    }
    const merged = { ...attributes };
    for (const [key, value] of Object.entries(correlation)) {
        if (merged[key] === undefined && isScalar(value) && !isSensitiveKey(key)) {
            const scalar = sanitizeScalar(value);
            if (scalar !== undefined) {
                merged[key] = scalar;
            }
        }
    }
    return limitAttributes(merged);
}
//# sourceMappingURL=client-serialization.js.map